import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ASSOCIATION_SOURCE_OPTIONS, AssociationSource } from './associationSourceMappings';

/**
 * Custom hook that returns translated association source labels
 *
 * @returns Record of AssociationSource to translated label strings
 */
export const useAssociationSourceLabels = () => {
  const { t } = useTranslation('multiselects');

  return useMemo(() => {
    const labels: Record<AssociationSource, string> = {
      [AssociationSource.APP]: t('association_source.driver_app'),
      [AssociationSource.MANUAL]: t('association_source.manual_association'),
      [AssociationSource.DASHBOARD]: t('association_source.dashboard_fixed_association'),
      [AssociationSource.IDENTIFIER]: t('association_source.key_or_rfid_card'),
      [AssociationSource.FACE_ID]: t('association_source.face_id'),
      [AssociationSource.NOT_IDENTIFIED]: t('association_source.no_driver_associated'),
    };

    return labels;
  }, [t]);
};

export interface MultiSelectAssociationSourceProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedTypes?: string[];
  onChange: (selectedTypes: string[]) => void;
}

const MultiSelectAssociationSource = ({ isOpen, size = 'md', selectedTypes = [], onChange }: MultiSelectAssociationSourceProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedTypes || []);
  const labels = useAssociationSourceLabels();

  const hasSameElements = selectedTypes?.length === draft?.length && selectedTypes.every((value) => draft.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedTypes || []);
  }, [selectedTypes]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.includes(type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('association_source.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={200}
              heightItem={28}
              data={ASSOCIATION_SOURCE_OPTIONS}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                  <Label htmlFor={item} className='ellipsis w-full overflow-hidden' title={t(labels[item])}>
                    {labels[item]}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('association_source.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('association_source.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='link' />}>
        {t('association_source.title')} {(selectedTypes?.length || 0) > 0 ? `(${selectedTypes?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectAssociationSource;

// Re-export everything from associationSourceMappings for backward compatibility
export {
  ASSOCIATION_SOURCE_OPTIONS,
  AssociationSource,
  associationSourceLabel,
  RawIdentificationSource,
  sourceFromIdentificationMethod,
  toIdentificationMethods,
} from './associationSourceMappings';
